/**
 * Businesses list page.
 * 
 * @module pages/BusinessesPage
 * @see CSDEV-146 - Businesses list: add last analysis + AI score columns and search by owner email
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable, SearchInput, Pagination, Card, Badge } from '../components/ui';
import { useBusinesses, useIndustries, usePagination, useAuditLog, useExclusionRules, type BusinessWithOwnerExtended } from '../hooks';
import { isExcludedUser } from '../config/excluded-users';
import { AuditActionTypes } from '../lib/audit';
import { formatDate, formatPercentage } from '../lib/utils';
import type { DataTableColumn } from '../types';

/**
 * Businesses list page component.
 * 
 * @returns Businesses page element
 */
export function BusinessesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [ownerEmailSearch, setOwnerEmailSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [internalFilter, setInternalFilter] = useState<'all' | 'external' | 'internal'>('external');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const { page, pageSize, goToPage, changePageSize, pageSizeOptions } = usePagination();
  const { logAction } = useAuditLog();

  // Get owner filter from URL params
  const ownerFilter = searchParams.get('owner') || '';

  const { data: industries } = useIndustries();
  const { data: exclusionRules, isLoading: exclusionRulesLoading } = useExclusionRules();

  // When internal/external filter is active, fetch all businesses so the
  // client-side filter operates on the full dataset instead of a single page.
  // Cap at 10,000 rows — if the businesses table exceeds this, move exclusion
  // filtering server-side (e.g., a database view or RPC).
  const isFilteringByInternal = internalFilter !== 'all';

  const { data, isLoading, error } = useBusinesses({
    page: isFilteringByInternal ? 1 : page,
    pageSize: isFilteringByInternal ? 10000 : pageSize,
    search,
    ownerId: ownerFilter || undefined,
    industry: industryFilter || undefined,
    ownerEmail: ownerEmailSearch || undefined,
    sortBy,
    sortOrder,
  });

  /**
   * Handles column sort clicks.
   * Toggles order if same column, resets to desc for new column.
   * @see CSDEV-147 - Wire up DataTable sorting
   */
  const handleSort = useCallback((key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('desc');
    }
    goToPage(1);
  }, [sortBy, sortOrder, goToPage]);

  // Log page view
  useEffect(() => {
    logAction(AuditActionTypes.VIEW_BUSINESSES_LIST);
  }, [logAction]);

  // Log search
  useEffect(() => {
    if (search) {
      logAction(AuditActionTypes.SEARCH_BUSINESSES, undefined, undefined, { search });
    }
  }, [search, logAction]);

  const handleRowClick = useCallback(
    (business: BusinessWithOwnerExtended) => {
      navigate(`/businesses/${business.id}`);
    },
    [navigate]
  );

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    goToPage(1);
  }, [goToPage]);

  const handleOwnerEmailSearch = useCallback((value: string) => {
    setOwnerEmailSearch(value);
    goToPage(1);
  }, [goToPage]);

  /**
   * Clears all filters.
   */
  const clearFilters = () => {
    setSearchParams({});
    setSearch('');
    setOwnerEmailSearch('');
    setIndustryFilter('');
    setInternalFilter('external');
    goToPage(1);
  };

  const filteredData = data?.data.filter((business) => {
    if (internalFilter === 'all') return true;
    const excluded = isExcludedUser(business.users?.email, exclusionRules);
    return internalFilter === 'internal' ? excluded : !excluded;
  });

  const hasActiveFilters = search || ownerFilter || industryFilter || ownerEmailSearch || internalFilter !== 'external';

  const columns: DataTableColumn<BusinessWithOwnerExtended>[] = [
    {
      key: 'business_name',
      header: 'Business Name',
      sortable: true,
      render: (business) => (
        <span className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{business.business_name}</span>
          {isExcludedUser(business.users?.email, exclusionRules) && (
            <Badge variant="neutral" size="sm">Internal</Badge>
          )}
        </span>
      ),
    },
    {
      key: 'users',
      header: 'Owner',
      render: (business) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (business.users?.id) {
              navigate(`/users/${business.users.id}`);
            }
          }}
          className="text-primary-600 hover:text-primary-700"
        >
          {business.users?.email || '—'}
        </button>
      ),
    },
    {
      key: 'industry',
      header: 'Industry',
      render: (business) => business.industry || '—',
    },
    {
      key: 'prompts',
      header: 'Prompts',
      align: 'center',
      render: (business) => (
        <span className="text-gray-500">
          {business.prompts?.length || 0}
        </span>
      ),
    },
    {
      key: 'last_analysis_date',
      header: 'Last Analysis',
      sortable: true,
      render: (business) => (
        <span className="text-gray-500">
          {business.last_analysis_date ? formatDate(business.last_analysis_date) : '—'}
        </span>
      ),
    },
    {
      key: 'latest_ai_score',
      header: 'AI Score',
      align: 'right',
      render: (business) => {
        if (business.latest_ai_score === null || business.latest_ai_score === undefined) {
          return <span className="text-gray-400">—</span>;
        }
        const score = business.latest_ai_score;
        const variant =
          score >= 0.7 ? 'success' :
          score >= 0.4 ? 'warning' :
          'error';
        return (
          <Badge variant={variant} size="sm">
            {formatPercentage(score, true)}
          </Badge>
        );
      },
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (business) => (
        <span className="text-gray-500">{formatDate(business.created_at)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Businesses</h1>
        <p className="text-gray-500">
          View all business configurations and their owners
        </p>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchInput
              placeholder="Search by business name..."
              onSearch={handleSearch}
              className="flex-1 max-w-xs"
            />
            <SearchInput
              placeholder="Search by owner email..."
              onSearch={handleOwnerEmailSearch}
              className="flex-1 max-w-xs"
            />
            <select
              value={industryFilter}
              onChange={(e) => {
                setIndustryFilter(e.target.value);
                goToPage(1);
              }}
              className="input w-full sm:w-48"
            >
              <option value="">All industries</option>
              {industries?.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
            <select
              value={internalFilter}
              onChange={(e) => {
                setInternalFilter(e.target.value as 'all' | 'external' | 'internal');
                goToPage(1);
              }}
              disabled={exclusionRulesLoading}
              className="input w-full sm:w-48"
            >
              <option value="all">All businesses</option>
              <option value="external">External only</option>
              <option value="internal">Internal only</option>
            </select>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="btn-secondary text-sm"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Error state */}
      {error && (
        <Card>
          <p className="text-error-600">Failed to load businesses: {error.message}</p>
        </Card>
      )}

      {/* Data table */}
      <Card padding="none">
        <DataTable
          columns={columns}
          data={filteredData || []}
          isLoading={isLoading}
          emptyMessage="No businesses found"
          onRowClick={handleRowClick}
          keyExtractor={(business) => business.id}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
        {data && isFilteringByInternal ? (
          <div className="px-4 py-3 text-sm text-gray-500 border-t border-gray-200">
            {filteredData?.length ?? 0} {internalFilter} business{(filteredData?.length ?? 0) !== 1 ? 'es' : ''} found
          </div>
        ) : data && data.totalPages > 0 ? (
          <Pagination
            currentPage={page}
            totalPages={data.totalPages}
            onPageChange={goToPage}
            pageSize={pageSize}
            onPageSizeChange={changePageSize}
            totalItems={data.count}
            pageSizeOptions={pageSizeOptions}
          />
        ) : null}
      </Card>
    </div>
  );
}
